import {
  addressSchema,
  frenchDepartmentCodeSchema,
  pictureSchema,
  signatureUrlSchema,
  toUtcCalendarDay,
} from '@omecare/utils';
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { EditUserProfileFormValues } from '@/components/forms/edit-user-profile/schema';
import http from '@/utils/http.service';

const editUserProfileResponseSchema = z.object({
  id: z.string().uuid(),
  firstname: z.string(),
  phoneNumber: z.string(),
  lastname: z.string(),
  picture: pictureSchema,
  address: addressSchema,
  orderRegistrationNumber: z.string(),
  rppsNumber: z.string(),
  signatureUrl: signatureUrlSchema,
  dateOfBirth: z.coerce.date(),
  thesis: z.boolean(),
  // Structured rhythm fields
  onSiteSlotsWithoutAssistant: z.number().int().nullable().default(null),
  remoteSlotsWithoutAssistant: z.number().int().nullable().default(null),
  onSiteSlotsWithAssistant: z.number().int().nullable().default(null),
  remoteSlotsWithAssistant: z.number().int().nullable().default(null),
  iban: z.string(),
  bic: z.string().nullable().default(null),
  urssafNumber: z.string().nullable().default(null),
  licenceNumber: z.string().nullable().default(null),
  licenceExpiresAt: z.coerce.date().nullable().default(null),
  licenceCouncilDepartmentCode: frenchDepartmentCodeSchema
    .nullable()
    .default(null),
  createdAt: z.coerce.date(),
});

export type EditUserProfileResponse = z.infer<
  typeof editUserProfileResponseSchema
>;
export type EditUserProfilePayload = {
  thesis: boolean;
  // Structured rhythm fields
  onSiteSlotsWithoutAssistant?: number | null;
  remoteSlotsWithoutAssistant?: number | null;
  onSiteSlotsWithAssistant?: number | null;
  remoteSlotsWithAssistant?: number | null;
  deleteAvatar: boolean;
  // Contract profile (issue #796)
  urssafNumber?: string | null;
  licenceNumber?: string | null;
  licenceExpiresAt?: Date | null;
  licenceCouncilDepartmentCode?: string | null;
} & Omit<EditUserProfileFormValues, 'picture' | 'thesis' | 'signatureUrl'>;

const editUserProfile = (payload: EditUserProfilePayload) => {
  return http.users
    .updateProfile({
      firstname: payload.firstname,
      lastname: payload.lastname,
      phoneNumber: payload.phoneNumber,
      dateOfBirth: payload.dateOfBirth,
      orderRegistrationNumber: payload.orderRegistrationNumber,
      rppsNumber: payload.rppsNumber,
      iban: payload.iban,
      bic: payload.bic,
      thesis: payload.thesis,
      onSiteSlotsWithoutAssistant: payload.onSiteSlotsWithoutAssistant,
      remoteSlotsWithoutAssistant: payload.remoteSlotsWithoutAssistant,
      onSiteSlotsWithAssistant: payload.onSiteSlotsWithAssistant,
      remoteSlotsWithAssistant: payload.remoteSlotsWithAssistant,
      file: payload.file,
      deleteAvatar: payload.deleteAvatar,
      signatureFile: payload.signatureFile ?? undefined,
      urssafNumber: payload.urssafNumber ?? null,
      licenceNumber: payload.licenceNumber ?? null,
      licenceExpiresAt: payload.licenceExpiresAt
        ? toUtcCalendarDay(payload.licenceExpiresAt)
        : null,
      licenceCouncilDepartmentCode:
        payload.licenceCouncilDepartmentCode ?? null,
      address: payload.address
        ? {
            ...payload.address,
            state: 'France',
          }
        : undefined,
    })
    .then<EditUserProfileResponse>(({ response, transform }) => {
      const user = transform.handle({
        response,
        attributes: {
          firstname: true,
          phoneNumber: true,
          lastname: true,
          picture: true,
          createdAt: true,
        },
        relations: {
          doctorSubstitute: true,
        },
      });

      return editUserProfileResponseSchema.parse({
        id: user.id,
        firstname: user.firstname,
        phoneNumber: user.phoneNumber,
        lastname: user.lastname,
        picture: user.picture,
        address: user.doctorSubstitute!.address,
        orderRegistrationNumber: user.doctorSubstitute!.orderRegistrationNumber,
        rppsNumber: user.doctorSubstitute!.rppsNumber,
        signatureUrl: user.doctorSubstitute!.signatureUrl,
        dateOfBirth: user.doctorSubstitute!.dateOfBirth,
        thesis: user.doctorSubstitute!.thesis,
        onSiteSlotsWithoutAssistant:
          user.doctorSubstitute!.onSiteSlotsWithoutAssistant,
        remoteSlotsWithoutAssistant:
          user.doctorSubstitute!.remoteSlotsWithoutAssistant,
        onSiteSlotsWithAssistant:
          user.doctorSubstitute!.onSiteSlotsWithAssistant,
        remoteSlotsWithAssistant:
          user.doctorSubstitute!.remoteSlotsWithAssistant,
        iban: user.doctorSubstitute!.iban,
        bic: user.doctorSubstitute!.bic,
        urssafNumber: user.doctorSubstitute!.urssafNumber,
        licenceNumber: user.doctorSubstitute!.licenceNumber,
        licenceExpiresAt: user.doctorSubstitute!.licenceExpiresAt,
        licenceCouncilDepartmentCode:
          user.doctorSubstitute!.licenceCouncilDepartmentCode,
        createdAt: user.createdAt,
      });
    });
};

const useEditUserProfileMutation = (): UseMutationResult<
  EditUserProfileResponse,
  Error,
  EditUserProfilePayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editUserProfile,
    onSuccess() {
      toast.success(t('http.edit-profile.success'));
      queryClient.invalidateQueries({
        queryKey: ['me'],
      });
    },
  });
};

export default useEditUserProfileMutation;
